import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { unparse } from 'papaparse';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import {
  useApiAdminlocalescustomPost,
  useApiAdminlocalescustomIdPatch,
  useApiAdminlocalescustomIdDelete,
  adminLocales
} from '@r/api/generated/client.ts';
import type {
  UserWorkspaceMeReadModel,
  LocaleLocaleAdminReadModel,
  AdminLocalesParams,
  LocaleCustomLocaleCustomWriteModel,
  LocaleCustomLocaleCustomEditJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import {
  useFilterFields,
  useTranslationColumns
} from '@r/components/translations/translation-columns.tsx';
import { downloadCSV } from '@r/utils/utils.ts';
import { useUser } from '@r/api/auth.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@r/components/ui/dialog.tsx';
import { LocaleToEditForm } from '@admin/components/forms/locale/locale-to-edit-form.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type UserWorkspace = UserWorkspaceMeReadModel;
type LocaleCollection = LocaleLocaleAdminReadModel;

export function Translations() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const userWorkspaces: UserWorkspace[] = user.data?.visibleWorkspaces ?? [];
  const activeWorkspace = getActiveWorkspaceUniversal(userWorkspaces);
  const isUserSuperAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN') || false;
  const isUserAdmin = activeWorkspace?.roles?.includes('ROLE_ADMIN') || isUserSuperAdmin || false;

  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [translationToEdit, setTranslationToEdit] = useState<LocaleCollection | null>(null);

  const filterFields = useFilterFields();

  const translationsTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('translations', user.data?.id),
    [user.data?.id]
  );

  function getTranslationDataForExport(locale: LocaleCollection) {
    return [locale.label, locale.content, locale.defaultContent, locale.isCustom];
  }

  // Helper function to build query params from filters and pagination
  function buildQueryParams(
    filters: ColumnFiltersState,
    pageIdx: number,
    pageSz: number
  ): AdminLocalesParams {
    const isCustomFilter = filters.find((filter) => filter.id === 'isCustom');
    const search = filters.find((filter) => filter.id === 'search');
    let isCustom: boolean | undefined;

    if (isCustomFilter?.value) {
      if (Array.isArray(isCustomFilter.value) && isCustomFilter.value.length > 0) {
        const filterStringValue = isCustomFilter.value[0];
        if (filterStringValue !== 'all') {
          isCustom = filterStringValue === 'true';
        }
      } else if (typeof isCustomFilter.value === 'string' && isCustomFilter.value !== 'all') {
        isCustom = isCustomFilter.value === 'true';
      }
    }

    const params: AdminLocalesParams = {
      page: pageIdx + 1,
      perPage: pageSz
    };

    if (isCustom !== undefined) {
      params.isCustom = isCustom;
    }

    if (search?.value) {
      params.search = search.value as string;
    }

    return params;
  }

  // Use useQuery with adminLocales - simple queryKey like users.tsx and points.tsx
  // Note: translationsListFilters() will be defined after the table is created
  const translationsListQuery = useQuery({
    queryKey: ['translations_list'],
    queryFn: async () => {
      // Build params from current table state (like users.tsx and points.tsx)
      // translationsListFilters() reads from closure (variables defined after table creation)
      const filters = translationsListFilters();
      const params = buildQueryParams(filters.columnFilters, filters.pageIndex, filters.pageSize);
      return await adminLocales(params);
    }
  });

  const editCustomLocaleMutation = useApiAdminlocalescustomIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('toast.success.custom.translation.updated'));
        translationsListQuery.refetch();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'patch', '/admin/locales/custom')) {
          showOrvalErrorToast(error);
        } else {
          toast.error(t('toast.error.label'));
        }
      }
    }
  });

  const createCustomLocaleMutation = useApiAdminlocalescustomPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('toast.success.custom.translation.created'));
        translationsListQuery.refetch();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'post', '/admin/locales/custom')) {
          showOrvalErrorToast(error);
        } else {
          toast.error(t('toast.error.label'));
        }
      }
    }
  });

  const rollbackCustomLocaleMutation = useApiAdminlocalescustomIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('toast.success.custom.translation.rolled.back'));
        translationsListQuery.refetch();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/locales/custom')) {
          showOrvalErrorToast(error);
        } else {
          toast.error(t('toast.error.label'));
        }
      }
    }
  });

  async function editCustomLocale(localeId: number, data: { content: string }) {
    const payload: LocaleCustomLocaleCustomEditJsonMergePatchModel = {
      content: data.content
    };
    editCustomLocaleMutation.mutate({ id: localeId.toString(), data: payload });
  }

  async function createCustomLocale(originalLocale: LocaleCollection, data: { content: string }) {
    const payload: LocaleCustomLocaleCustomWriteModel = {
      locale: `/locales/${originalLocale.id}`,
      content: data.content
    };
    createCustomLocaleMutation.mutate({ data: payload });
  }

  async function rollbackCustomLocale(localeId: number) {
    rollbackCustomLocaleMutation.mutate({ id: localeId.toString() });
  }

  const onEditCustom = (locale: LocaleCollection) => {
    setTranslationToEdit(locale);
    setEditModalOpen(true);
  };

  const onCreateCustom = (locale: LocaleCollection) => {
    setTranslationToEdit(locale);
    setEditModalOpen(true);
  };

  const onRollbackCustom = (locale: LocaleCollection) => {
    toast(`${t('admin.translations.rollback.confirm')} "${locale.label}" ?`, {
      action: {
        label: t('label.confirm'),
        onClick: () => rollbackCustomLocale(locale.id ?? 0)
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  // Update table data and columns after query is ready
  const tableData = translationsListQuery.data?.data ?? [];
  const tablePageCount = translationsListQuery.data?.pagination?.last ?? 1;
  const tableColumns = useTranslationColumns(
    isUserAdmin,
    onEditCustom,
    onCreateCustom,
    onRollbackCustom
  );

  // Create final table with actual data and columns
  const { table } = useDataTable({
    data: tableData,
    columns: tableColumns,
    filterFields,
    pageCount: tablePageCount,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: translationsTablePersistenceKey
    }
  });

  // Get pagination/filter state from final table (like users.tsx and points.tsx)
  const { pageIndex, pageSize } = table.getState().pagination;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  // Helper function to get current filters from table state (like users.tsx and points.tsx)
  // This function reads from closure - variables defined above
  function translationsListFilters() {
    return { columnFilters, pageIndex, pageSize };
  }

  async function exportData() {
    setIsExporting(true);

    let allData: LocaleCollection[] = [];
    const limit = 500;

    const totalPageCount = Math.ceil(
      (translationsListQuery.data?.pagination?.totalItems ?? 1) / limit
    );

    // Use current table state for export filters
    const currentColumnFilters = table.getState().columnFilters;
    const isCustomFilter = currentColumnFilters.find((filter) => filter.id === 'isCustom');
    const search = currentColumnFilters.find((filter) => filter.id === 'search');
    let isCustom: boolean | undefined;

    if (isCustomFilter?.value) {
      if (Array.isArray(isCustomFilter.value) && isCustomFilter.value.length > 0) {
        const filterStringValue = isCustomFilter.value[0];
        if (filterStringValue !== 'all') {
          isCustom = filterStringValue === 'true';
        }
      } else if (typeof isCustomFilter.value === 'string' && isCustomFilter.value !== 'all') {
        isCustom = isCustomFilter.value === 'true';
      }
    }

    const exportFilters: AdminLocalesParams = {
      page: 1,
      perPage: limit
    };

    if (isCustom !== undefined) {
      exportFilters.isCustom = isCustom;
    }

    if (search?.value) {
      exportFilters.search = search.value as string;
    }

    const promises = Array.from({ length: totalPageCount }, (_, i) => {
      const params: AdminLocalesParams = {
        ...exportFilters,
        page: i + 1,
        perPage: limit
      };
      return adminLocales(params);
    });

    const pagesData = await Promise.all(promises);
    allData = pagesData.flatMap((pageData) => pageData.data ?? []);

    const csv = unparse({
      fields: [t('label.translation.label'), t('label.translation.content')],
      data: allData.map((translation) => getTranslationDataForExport(translation))
    });

    downloadCSV(csv, `translations-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`);
    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      translationsListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting, translationsListQuery]);

  return (
    <Fragment>
      <div className="space-y-2.5">
        <h2 className="text-4xl font-bold">{t('admin.translations.title')}</h2>
        <p className="font-light text-muted-foreground">{t('admin.translations.description')}</p>
      </div>

      {translationsListQuery.isLoading && (
        <DataTableSkeleton
          columnCount={10}
          cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
        />
      )}

      {translationsListQuery.isSuccess && (
        <Fragment>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />
          <DataTable
            table={table}
            totalItems={translationsListQuery.data?.pagination?.totalItems}
          />
        </Fragment>
      )}

      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="p-14">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {translationToEdit?.isCustom
                ? t('admin.translations.edit.custom.title')
                : translationToEdit?.isCustom === false
                  ? t('admin.translations.create.custom.title')
                  : null}
            </DialogTitle>

            <DialogDescription>{t('translation.reminder.title')}</DialogDescription>
            <DialogDescription className="mt-2">
              <span className="flex flex-col gap-y-1 pl-5 space-y-1 text-start">
                <span>• {t('translation.reminder.code')}</span>
                <span>• {t('translation.reminder.value')}</span>
              </span>
            </DialogDescription>
          </DialogHeader>

          {translationToEdit && (
            <LocaleToEditForm
              locale={
                translationToEdit as unknown as Parameters<typeof LocaleToEditForm>[0]['locale']
              }
              onEditCustom={editCustomLocale}
              onCreateCustom={createCustomLocale}
              onSuccess={() => {
                setEditModalOpen(false);
                setTranslationToEdit(null);
                translationsListQuery.refetch();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
